export type ColorId = "red" | "blue" | "green" | "yellow" | "purple";

export const COLORS: ColorId[] = ["red", "blue", "green", "yellow", "purple"];



export const CAPACITY_L = 5 as const;
export const LAYER_L = 1 as const;
export const SLOT_COUNT = 2 as const;

export type BottleId = number;

export interface Bottle {
  id: BottleId;
  layers: ColorId[];
  sealed: boolean;
  visibility: number;
}

export interface PackingSlots {
  slots: Array<ColorId | null>;
  queue: ColorId[];
}

export type Phase =
  | "idle"
  | "selectSource"
  | "pouring"
  | "sealing"
  | "packing"
  | "win"
  | "lose"
  | "hint";

export interface GameState {
  level: number;
  seed: number;
  bottles: Bottle[];  // Currently visible/active bottles
  hiddenBottles: Bottle[];  // Reserved bottles that appear as visible ones are packed
  packing: PackingSlots;
  score: number;
  moves: MoveRecord[];
  phase: Phase;
  initialVisibleCount: number;  // Track how many bottles were initially visible
}

export interface PourMove {
  kind: "pour";
  from: BottleId;
  to: BottleId;
  color: ColorId;
  amount: number;
}

export interface SealEvent {
  bottleId: BottleId;
  prevSealed: boolean;
  nextSealed: boolean;
}

export interface PackEvent {
  bottleId: BottleId;
  color: ColorId;
  slotIndex: number;
  prevSlot: ColorId | null;
  nextSlot: ColorId | null;
  consumedQueue: boolean;
  scoreDelta: number;
}

export interface MoveRecord {
  move: PourMove;
  seals: SealEvent[];
  pack?: PackEvent;
  undo: UndoPatch;
}

export interface UndoPatch {
  bottles: Array<{ id: BottleId; layers: ColorId[]; sealed: boolean; visibility: number }>;
  packingSlots: Array<ColorId | null>;
  packingQueue: ColorId[];
  score: number;
  phase: Phase;
  hiddenBottles?: Array<{ id: BottleId; layers: ColorId[]; sealed: boolean; visibility: number }>;
}

export interface LevelConfig {
  level: number;
  seed?: number;
  colors: ColorId[];
  capacity: number;
  slotCount: number;
  totalPacks: number;
  emptyBottleCount: number;
  scrambleSteps: number;
  hiddenBottleRatio: number;  // Ratio of bottles with hidden layers
  hiddenVisibleLayers: 1 | 2;  // How many layers are visible in hidden bottles
  // New: Progressive unlock settings
  initialVisibleBottles: number;  // How many bottles to show initially
  reserveBottleCount: number;  // How many extra bottles to generate as reserve
  slotPolicy: SlotPolicy;
  maxReroll: number;
  solvability: SolvabilityConfig;
}

export type SlotPolicy =
  | { kind: "uniform" }
  | { kind: "biased"; biasColor: ColorId; biasStrength: number }
  | { kind: "delayed"; delayedColor: ColorId; delayUntilRatio: number }
  | { kind: "sequence"; sequence: ColorId[] };

export interface SolvabilityConfig {
  validate: boolean;
  maxNodes: number;
  timeBudgetMs: number;
  maxDepth: number;
}

export interface RuleResult<T> {
  ok: boolean;
  value?: T;
  reason?: string;
}

export interface PourPlan {
  from: BottleId;
  to: BottleId;
  color: ColorId;
  amount: number;
}
